import appConfig from '@/features/app/constants/app-config';
import useAppSelector from '@/features/app/hooks/useAppSelector';
import useAdminSettingQuery from '@/features/app/queries/useAdminSettingsQuery';
import useLogoutUser from '@/features/auth/hooks/useLogoutUser';
import useCartDisplayEntries from '@/features/cart/hooks/useCartDisplayEntries';
import supportedLanguages from '@/features/i18n/constants/supported-languages';
import useMarkNotificationAsReadMutation from '@/features/notifications/mutations/useMarkNotificationAsReadMutation';
import useNotificationsQuery from '@/features/notifications/queries/useNotificationsQuery';
import CustomTrans from '@/shared/components/custom-trans';
import { _unixToClientTime } from '@/shared/functions/timezone';
import { PhoneOutlined } from '@ant-design/icons';
import { cx as classNames } from '@emotion/css';
import {
  Button,
  IconArrowDown,
  IconBell,
  IconBox,
  IconCalandar,
  IconCart,
  IconExclamation,
  IconGear,
  IconGrid,
  IconHamberger,
  IconLogout,
  IconPlus,
  IconUser,
  Loader,
  Select,
} from '@thisisdmg/tirecloud-patterns';
import { Modal } from '@thisisdmg/tirecloud-patterns/v2';
import { IconMessage } from '@thisisdmg/tirecloud-patterns/v2/components/icons';
import { App, Collapse, Dropdown, Grid, MenuProps, Space, Tooltip } from 'antd';
import { ItemType } from 'antd/es/menu/interface';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation, useSearchParams } from 'react-router-dom';
import { match } from 'ts-pattern';
import { StringParam, useQueryParam } from 'use-query-params';
import useReportBugButton from '@/features/sentry/components/report-bug-button';
import CookieConsentModal from '@/features/app/components/consent-modal';
import ContactUsModal from './contact-us-modal';
import DeFlag from './flags/de-flag';
import EnFlag from './flags/en-flag';
import HeFlag from './flags/he-flag';
import Search from './search';
import { useUserSideMenu } from '@/features/user/components/side-menu/get-sidemenu';

function getFlag(lang: string) {
  switch (lang) {
    case 'en':
      return <EnFlag />;
    case 'de':
      return <DeFlag />;
    case 'he':
      return <HeFlag />;
    default:
      return null;
  }
}

function Header() {
  const { user } = useAppSelector((state) => state.auth);
  const { t, i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const theme = user === null ? 'light' : 'dark';
  const { notification } = App.useApp();
  const location = useLocation();
  const cartEntries = useCartDisplayEntries();
  const reportBugButtonRef = useReportBugButton();

  const { data: notifications, isError } = useNotificationsQuery(
    {
      // max signed int 32 length
      limit: 2 ** 31 - 1,
    },
    {
      enabled: user?.__role === 'ROLE_WEBSHOP_USER',
    }
  );

  useEffect(() => {
    if (isError) {
      notification.error({
        key: 'APP005',
        message: <CustomTrans i18nKey={'component.header.feedback.notificationFetchError'} />,
        duration: 0,
      });
    }
  }, [isError, notification]);

  const unreadNotiCount = (notifications ?? []).filter((item) => Boolean(item.isNew)).length;
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const breakpoint = Grid.useBreakpoint();
  const { data: adminSettings } = useAdminSettingQuery();
  const markNotificationAsReadMutation = useMarkNotificationAsReadMutation();
  const logoutUser = useLogoutUser();

  useEffect(() => {
    const navLinkClick = () => {
      setMobileMenuOpen(false);
    };

    document.addEventListener('click', (event) => {
      if ((event.target as Element).closest('.mobile-nav-link .nav-link')) {
        navLinkClick();
      }
    });

    return () => {
      document.removeEventListener('click', (event) => {
        if ((event.target as Element).closest('.mobile-nav-link .nav-link')) {
          navLinkClick();
        }
      });
    };
  }, [setMobileMenuOpen]);

  const markAllAsRead = useCallback(() => {
    const unreadNotifications = notifications?.filter((item) => Boolean(item.isNew)) ?? [];

    Promise.all(
      unreadNotifications.map((item) => markNotificationAsReadMutation.mutateAsync(item.id))
    )
      .then(() => {
        notification.success({
          key: 'NOTI001',
          message: t('component.header.feedback.markAllAsReadSuccess'),
        });
      })
      .catch(() => {
        notification.error({
          key: 'NOTI001',
          message: t('component.header.feedback.failedToMarkAsRead'),
        });
      });
  }, [notifications, markNotificationAsReadMutation, notification, t]);

  const notificationItemList = useMemo((): ItemType[] => {
    const items = (notifications ?? [])
      .filter((item) => Boolean(item.isNew))
      .slice(0, 3)
      .map((item): ItemType => {
        return {
          type: 'item',
          label: (
            <Link
              to={item.link ? item.link : '#'}
              role="button"
              onClick={(e) => {
                if (!item.link) e.preventDefault();
              }}
              className="noti-link"
            >
              <div className={classNames('noti-item', item.isNew ? 'status-new' : '')}>
                <p className={'noti-type'}>{item.notifyRegarding}</p>
                <h5>
                  <span>{item.title}</span>
                  {Boolean(item.isNew) && (
                    <span className={'status'}>{t('page.notifications.new')}</span>
                  )}
                </h5>
                <p className="noti-des">{item.description}</p>
                <small className="date tw:mb-(--spacing-2)">
                  {_unixToClientTime(item.creationDate).format('LLL')}
                </small>
                <div className="noti-button-group">
                  <Button
                    color="success-outline"
                    size="s"
                    disabled={!item.isNew}
                    onClick={() => {
                      markNotificationAsReadMutation.mutate(item.id, {
                        onError() {
                          notification.error({
                            key: 'NOTI001',
                            message: (
                              <CustomTrans i18nKey="component.header.feedback.failedToMarkAsRead" />
                            ),
                          });
                        },
                      });
                    }}
                  >
                    {t('page.notifications.markAsRead')}
                  </Button>
                  {/* <Button color="error-outline" size="s">
                    Clear
                  </Button> */}
                </div>
              </div>
            </Link>
          ),
          key: `noti-item-${item.id}`,
        };
      });

    return [
      ...(items.length > 0
        ? items
        : ([
            {
              type: 'item',
              label: (
                <div className="noti-item">
                  <p className="tw:mb-0 tw:text-(--shade-600)">
                    {t('component.header.notification.noNewNotification')}
                  </p>
                </div>
              ),
              key: '2',
            },
          ] as ItemType[])),
      {
        type: 'divider',
        key: '3',
      },
      {
        type: 'item',
        label: (
          <div className="tw:flex tw:items-center tw:justify-between tw:gap-(--spacing-2)">
            <Link to="/notifications">{t('component.header.notification.viewAll')}</Link>
            {unreadNotiCount > 0 && (
              <Button size="s" color="success-outline" onClick={markAllAsRead}>
                {t('component.header.notification.markAllAsRead')}
              </Button>
            )}
          </div>
        ),
        key: '4',
      },
    ];
  }, [
    notifications,
    t,
    markAllAsRead,
    markNotificationAsReadMutation,
    notification,
    unreadNotiCount,
  ]);

  const leftNavGroupItems = useMemo(() => {
    if (!user || !user.__role) return [];

    return match(user.__role)
      .with('ROLE_WEBSHOP_USER', () => [
        {
          label: t('component.header.leftMenu.dashboard'),
          icon: <IconGrid />,
          url: '/dashboard',
        },
        {
          label: t('component.header.leftMenu.products'),
          icon: <IconBox />,
          url: '/products',
        },
        {
          label: t('component.header.leftMenu.reservedStocks'),
          icon: <IconCalandar />,
          url: '/reserved-stocks',
        },
      ])
      .with('ROLE_ADMIN', () => [
        {
          label: t('component.header.leftMenu.dashboard'),
          icon: <IconGrid />,
          url: appConfig.defaultAdminPage,
        },
      ])
      .otherwise(() => []);
  }, [user, t]);

  const userDropdownMenuItems = useMemo(() => {
    const menuItems = match(user?.__role)
      .with('ROLE_WEBSHOP_USER', () => [
        {
          label: (
            <Link to="/orders" className="icon-link">
              <IconBox />
              <span>{t('component.header.userMenu.orders')}</span>
            </Link>
          ),
          key: '/orders',
        },
        {
          label: (
            <Link to="/user/notification-settings" className="icon-link">
              <IconGear />
              <span>{t('component.header.userMenu.settings')}</span>
            </Link>
          ),
          key: '/user/notification-settings',
        },
      ])
      .otherwise(() => []);

    return [
      {
        label: (
          <Link to="/user/profile" className="icon-link">
            <IconUser />
            <span>{t('component.header.userMenu.profile')}</span>
          </Link>
        ),
        key: '1',
      },
      ...menuItems,
      {
        type: 'divider',
      },
      {
        label: (
          <button
            className="icon-link tw:bg-transparent tw:border-none tw:cursor-pointer user-account-dropdown-logout-button"
            onClick={() => {
              setLogoutModalOpen(true);
            }}
          >
            <span className="tw:rtl:transform tw:rtl:rotate-180">
              <IconLogout />
            </span>
            <span>{t('component.header.userMenu.logoutBtn')}</span>
          </button>
        ),
        key: '4',
      },
    ];
  }, [user, t]);

  const [contactUsModalOpen, setContactUsModalOpen] = useState(false);
  const userSideMenu = useUserSideMenu();
  const [usedVoucher] = useQueryParam('voucher', StringParam);
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(
    localStorage.getItem(appConfig.cookieConsentKey) === null
  );
  const [, setSearchParams] = useSearchParams();
  const breakpoints = Grid.useBreakpoint();
  // const adminSideMenu =

  useEffect(() => {
    const updateCookieConsent = () => {
      setIsConsentModalOpen(localStorage.getItem(appConfig.cookieConsentKey) === null);
    };
    window.addEventListener('app:cookieConsentUpdated', updateCookieConsent);
    return () => {
      window.removeEventListener('app:cookieConsentUpdated', updateCookieConsent);
    };
  }, []);

  return (
    <Fragment>
      <CookieConsentModal
        isOpen={
          !(breakpoints.xxl ?? false) &&
          isConsentModalOpen &&
          location.pathname !== '/privacy-policy'
        }
        onClose={() => {
          setIsConsentModalOpen(false);
        }}
      />
      {/* {adminSettinFetching ? <Loader fullScreen /> : null} */}
      {markNotificationAsReadMutation.isPending && <Loader fullScreen />}
      <Modal
        open={logoutModalOpen}
        closeButtonProps={false}
        title={t('component.header.userMenu.logoutBtn')}
        okText={t('component.header.userMenu.confirmLogoutBtn')}
        cancelText={t('component.header.userMenu.cancelLogoutBtn')}
        okButtonProps={{
          color: 'brand',
        }}
        onOk={() => {
          if (window._paq) {
            window._paq.push(['trackEvent', 'User', 'Click', 'Logout']);
          }
          logoutUser(() => {
            setSearchParams({ action: 'logout' });
          }).then(() => {
            setLogoutModalOpen(false);
          });
        }}
        onCancel={() => {
          setLogoutModalOpen(false);
        }}
        className="logout-modal"
      >
        {t('component.header.userMenu.logoutConfirmation')}
      </Modal>
      <ContactUsModal open={contactUsModalOpen} onClose={() => setContactUsModalOpen(false)} />
      <nav
        className={classNames(
          `main-header bg-${theme} tw:h-[92px]`,
          theme === 'light' && 'tw:bg-white'
        )}
      >
        <ul className="nav-list tw:mb-0">
          <li className="nav-logo">
            <Link
              className={classNames(
                'navbar-brand main-logo',
                theme === 'dark' &&
                  !adminSettings?.logoLightVariant?.fullpath &&
                  'tw:!bg-white tw:px-(--spacing-4) tw:py-(--spacing-2)',
                theme === 'light' &&
                  !adminSettings?.logoDarkVariant?.fullpath &&
                  'tw:!bg-black tw:px-(--spacing-4) tw:py-(--spacing-2)'
              )}
              to="/"
            >
              <img
                src={`${appConfig.apiUrl}/var/assets${
                  theme === 'dark'
                    ? (adminSettings?.logoLightVariant?.fullpath ??
                      adminSettings?.logoDarkVariant?.fullpath)
                    : (adminSettings?.logoDarkVariant?.fullpath ??
                      adminSettings?.logoLightVariant?.fullpath)
                }`}
                alt="Tirecloud"
              />
            </Link>
          </li>
          {(user === null ? [] : leftNavGroupItems).map((item, i: number) => (
            <li className="nav-item" key={i}>
              <Tooltip title={breakpoint.xxl ? undefined : item.label} arrow placement="bottom">
                <NavLink className={`nav-link`} to={item.url}>
                  {item.icon}
                  <span className="nav-text" data-text={item.label}>
                    {item.label}
                  </span>
                </NavLink>
              </Tooltip>
            </li>
          ))}
        </ul>
        {user !== null && user.__role === 'ROLE_WEBSHOP_USER' && <Search />}
        <ul className="site-setting tw:mb-0">
          <div className="tw:items-center tw:gap-x-6 tw:!mr-(--spacing-7) tw:hidden tw:xl:flex">
            <li className="nav-item tw:list-none tw:mr-(--spacing-4)">
              <Link className="nav-link" to="/about">
                {t('component.header.other.aboutUs')}
              </Link>
            </li>
            <li className="nav-item tw:list-none">
              <Link className="nav-link" to="/brand">
                {t('component.header.other.brand')}
              </Link>
            </li>
            <li className="nav-item tw:list-none">
              <button
                className="nav-link tw:cursor-pointer tw:bg-transparent tw:border-none"
                onClick={() => setContactUsModalOpen(true)}
              >
                {t('component.header.other.contact')}
              </button>
            </li>
          </div>
          <li className="nav-item tw:list-none nav-lang">
            <Tooltip title={t('component.header.other.changeLanguage')} arrow placement="bottom">
              <Select
                className="select-langs"
                placeholder="select one country"
                value={i18n.language}
                onChange={(value) => {
                  i18n.changeLanguage(value);
                }}
                optionLabelProp="label"
                popupClassName="select-lang-popup"
                data-test-id="header-language-changer"
                // options={Object.entries(supportedLanguages).map(([key, lang]) => {
                //   return {
                //     label: (
                //       <Space>
                //         <span role="img" aria-label={key}>
                //           {getFlag(key)}
                //         </span>
                //         <span>{key.toUpperCase()}</span>
                //       </Space>
                //     ),
                //     value: key,
                //     children: [
                //       <Space key={key}>
                //         <span role="img" aria-label="en">
                //           {getFlag(key)}
                //         </span>
                //         <span>{lang.name}</span>
                //       </Space>,
                //     ],
                //   };
                // })}
                options={Object.entries(supportedLanguages).map(([key]) => {
                  return {
                    key,
                    value: key,
                    label: (
                      <>
                        <span role="img" aria-label={key}>
                          {getFlag(key)}
                        </span>
                        <span>{key.toUpperCase()}</span>
                      </>
                    ),
                  };
                })}
                optionRender={(option) => {
                  const language =
                    supportedLanguages[option.value as keyof typeof supportedLanguages];
                  return (
                    <Space>
                      <span role="img" aria-label={option.value as string}>
                        {getFlag(option.value as string)}
                      </span>
                      <span>{language?.name}</span>
                    </Space>
                  );
                }}
              />
            </Tooltip>
          </li>
          {user !== null && (
            <>
              <li className="nav-item">
                <Dropdown
                  overlayClassName="notification-dropdown-over"
                  menu={{ items: notificationItemList }}
                  trigger={['click']}
                >
                  <Tooltip
                    title={t('component.header.other.notification')}
                    arrow
                    placement="bottom"
                  >
                    <Button
                      className="btn-icon badge notification-dropdown-trigger"
                      icon={<IconBell />}
                    >
                      {unreadNotiCount > 0 && <span className="qtylable">{unreadNotiCount}</span>}
                    </Button>
                  </Tooltip>
                </Dropdown>
              </li>
              {user.__role === 'ROLE_WEBSHOP_USER' && (
                <li className="nav-item">
                  <Link className="" to={`/cart${usedVoucher ? `?voucher=${usedVoucher}` : ''}`}>
                    <Tooltip title={t('component.header.other.cart')} arrow placement="bottom">
                      <Button className="btn-icon badge" icon={<IconCart />}>
                        <span className="qtylable">{cartEntries.length ?? 0}</span>
                      </Button>
                    </Tooltip>
                  </Link>
                </li>
              )}
              <li className="nav-item nav-user">
                <Dropdown
                  menu={{ items: userDropdownMenuItems as MenuProps['items'] }}
                  trigger={['click']}
                  overlayClassName="nav-user-popup"
                >
                  <div className="user-setting">
                    {/* <img src={UserImg} className="user-img" /> */}
                    {/* <div className="user-img tw:bg-(--shade-600)" /> */}
                    <div className="user-info">
                      <h5>{user?.about.fullName}</h5>
                      <span>@{user?.username}</span>
                    </div>
                    <IconArrowDown color="#8C887F" />
                  </div>
                </Dropdown>
              </li>
            </>
          )}
          <li className="nav-item tw:list-none nav-hamberger">
            <Button
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              className={classNames('btn-icon', theme === 'light' && 'tw:!text-black')}
              icon={<IconHamberger size={24} />}
              aria-label="Mobile Menu Button"
            ></Button>
          </li>
        </ul>
      </nav>

      {breakpoint.xs &&
      user !== null &&
      user.__role === 'ROLE_WEBSHOP_USER' &&
      !location.pathname.startsWith('/orders') ? (
        <Search isMobileSearch={true} />
      ) : null}
      <div className={`mobile-nav-content ${mobileMenuOpen ? 'open' : ''}`}>
        <Button
          onClick={() => setMobileMenuOpen(false)}
          className="close-btn"
          aria-label="Close Mobile Menu Button"
          icon={<IconPlus size={24} />}
        />
        <>
          <ul className="mobile-nav-link">
            {user !== null && (
              <>
                {leftNavGroupItems?.map((item, i: number) => (
                  <li className="nav-item" key={i}>
                    <NavLink className={`nav-link`} to={item.url}>
                      {item.icon}
                      <span className="nav-text" data-text={item.label}>
                        {item.label}
                      </span>
                    </NavLink>
                  </li>
                ))}
                <li className="nav-item">
                  <Link to="/orders" className="nav-link">
                    <IconBox />
                    <span className="nav-text">{t('component.header.userMenu.orders')}</span>
                  </Link>
                </li>
                <li className="nav-item">
                  <Collapse
                    expandIcon={() => <IconArrowDown size={24} />}
                    expandIconPosition="end"
                    ghost
                    items={[
                      {
                        key: 1,
                        label: (
                          <a className="nav-link tw:pointer-events-none">
                            <IconUser />
                            <span className="nav-text">{t('component.header.other.account')}</span>
                          </a>
                        ),
                        children: (
                          <ul className="nav-links">
                            {userSideMenu
                              .filter((item) => item.key !== '/user/notification-settings')
                              .map((item) => (
                                <li className="nav-link" key={item.key}>
                                  <Link className="nav-link" to={item.key}>
                                    {item.icon}
                                    <span className="nav-text">{item.label}</span>
                                  </Link>
                                </li>
                              ))}
                          </ul>
                        ),
                      },
                    ]}
                  />
                </li>
                <li className="nav-item">
                  <Link to="/user/notification-settings" className="nav-link">
                    <span>
                      <IconGear size={20} />
                    </span>
                    <span className="nav-text">
                      {t('component.header.userMenu.notificationSettings')}
                    </span>
                  </Link>
                </li>
              </>
            )}
            <li className="nav-item">
              <Collapse
                expandIcon={() => <IconArrowDown size={24} />}
                expandIconPosition="end"
                ghost
                className="nav-resource-collapse"
                items={[
                  {
                    key: 1,
                    label: (
                      <a className="nav-link tw:pointer-events-none">
                        <IconUser />
                        <span className="nav-text">{t('component.header.other.resources')}</span>
                      </a>
                    ),
                    children: (
                      <ul className="nav-links">
                        <li className="nav-link">
                          <Link className="nav-link" to="/about">
                            <IconExclamation />
                            <span className="nav-text">{t('component.header.other.aboutUs')}</span>
                          </Link>
                        </li>
                        <li className="nav-link">
                          <Link className="nav-link" to="/brand">
                            <IconExclamation />
                            <span className="nav-text">{t('component.header.other.brand')}</span>
                          </Link>
                        </li>
                        <li className="nav-link">
                          <button
                            className="nav-link tw:cursor-pointer tw:bg-transparent tw:border-none"
                            onClick={() => setContactUsModalOpen(true)}
                          >
                            <PhoneOutlined />
                            <span className="nav-text">{t('component.header.other.contact')}</span>
                          </button>
                        </li>
                      </ul>
                    ),
                  },
                ]}
              />
            </li>
            <li className="nav-item">
              <button
                ref={reportBugButtonRef}
                className="nav-link tw:bg-transparent tw:border-none"
              >
                <IconMessage />
                {t('component.sentryFeedback.triggerLabel')}
              </button>
            </li>
            {user !== null && (
              <li className="nav-item">
                <button
                  className="nav-link tw:bg-transparent tw:border-none"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setLogoutModalOpen(true);
                  }}
                >
                  <span className="tw:rtl:transform tw:rtl:rotate-180">
                    <IconLogout />
                  </span>
                  <span className="nav-text">{t('component.header.userMenu.logoutBtn')}</span>
                </button>
              </li>
            )}
          </ul>
        </>
        <ul className="mobile-lang">
          {Object.entries(supportedLanguages).map(([key, lang]) => {
            return (
              <li key={key}>
                <Button
                  color={`${i18n.language == key ? 'primary' : 'secondary'}`}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    i18n.changeLanguage(key);
                  }}
                  aria-label={lang.name}
                  className={classNames(i18n.language === key && 'tw:!text-black')}
                >
                  {getFlag(key)}
                  <span>{lang.shortCode.toUpperCase()}</span>
                </Button>
              </li>
            );
          })}
        </ul>
        <div className="tw:mt-auto tw:flex tw:flex-col tw:gap-y-(--spacing-2)">
          <Link to="/privacy-policy" className="tw:text-(--shade-400) tw:hover:text-(--shade-400)">
            {t('component.footer.privacyPolicy')}
          </Link>
          <Button
            color="link"
            onClick={() => {
              setMobileMenuOpen(false);
              setIsConsentModalOpen(true);
            }}
            className="tw:!text-(--shade-400) tw:hover:!text-(--shade-400) tw:w-fit tw:!px-0"
          >
            {t('component.footer.cookieConsent')}
          </Button>
        </div>
      </div>
    </Fragment>
  );
}

export default Header;
